import { getAuth } from 'firebase/auth';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { app } from '../firebase';
import './FollowButton.css';

const auth = getAuth(app);
const db = getFirestore(app);

const FollowButton = ({ targetUserId, targetUserName, currentUserFollowing = [], onFollowChange }) => {
    const [loading, setLoading] = useState(false);
    const [isFollowing, setIsFollowing] = useState(currentUserFollowing.includes(targetUserId));

    useEffect(() => {
        setIsFollowing(currentUserFollowing.includes(targetUserId));
    }, [currentUserFollowing, targetUserId]);

    const handleFollow = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const user = auth.currentUser;
        if (!user) {
            alert('Please log in to follow artists.');
            return;
        }

        if (user.uid === targetUserId) {
            return; // Can't follow yourself
        }

        setLoading(true);
        try {
            const newIsFollowing = !isFollowing;
            
            // Update current user's following list
            const currentUserProfileRef = doc(db, 'profiles', user.uid);
            const currentUserProfile = await getDoc(currentUserProfileRef);
            
            let currentFollowing = [];
            if (currentUserProfile.exists()) {
                currentFollowing = Array.isArray(currentUserProfile.data().following) 
                    ? currentUserProfile.data().following 
                    : [];
            }

            let updatedFollowing;
            if (newIsFollowing) {
                // Follow: add to following list
                updatedFollowing = [...currentFollowing.filter(id => id !== targetUserId), targetUserId];
            } else {
                // Unfollow: remove from following list
                updatedFollowing = currentFollowing.filter(id => id !== targetUserId);
            }

            await setDoc(currentUserProfileRef, {
                ...(currentUserProfile.exists() ? currentUserProfile.data() : {}),
                following: updatedFollowing
            }, { merge: true });

            // Update target user's followers list
            const targetUserProfileRef = doc(db, 'profiles', targetUserId);
            const targetUserProfile = await getDoc(targetUserProfileRef);
            
            let currentFollowers = [];
            if (targetUserProfile.exists()) {
                currentFollowers = Array.isArray(targetUserProfile.data().followers) 
                    ? targetUserProfile.data().followers 
                    : [];
            }

            let updatedFollowers;
            if (newIsFollowing) {
                // Follow: add to target's followers
                updatedFollowers = [...currentFollowers.filter(id => id !== user.uid), user.uid];
            } else {
                // Unfollow: remove from target's followers
                updatedFollowers = currentFollowers.filter(id => id !== user.uid);
            }

            await setDoc(targetUserProfileRef, {
                ...(targetUserProfile.exists() ? targetUserProfile.data() : {}),
                followers: updatedFollowers
            }, { merge: true });

            setIsFollowing(newIsFollowing);
            if (onFollowChange) {
                onFollowChange(targetUserId, newIsFollowing);
            }
        } catch (err) {
            console.error('Error following/unfollowing user:', err);
            alert('Failed to update follow status. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const user = auth.currentUser;
    if (!user || user.uid === targetUserId) {
        return null; // Don't show button if not logged in or trying to follow yourself
    }

    return (
        <button
            className={`follow-button-small ${isFollowing ? 'following' : ''}`}
            onClick={handleFollow}
            disabled={loading}
            type="button"
            title={isFollowing ? 'Unfollow' : 'Follow'}
        >
            {loading ? (
                <span className="follow-spinner"></span>
            ) : (
                <>
                    {isFollowing ? (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                            Following
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                <circle cx="8.5" cy="7" r="4"/>
                                <line x1="20" y1="8" x2="20" y2="14"/>
                                <line x1="23" y1="11" x2="17" y2="11"/>
                            </svg>
                            Follow
                        </>
                    )}
                </>
            )}
        </button>
    );
};

export default FollowButton;



